<template>
  <div class="map-card">
    <h5>Map of Vouchers Throughout California</h5>
    <div class="map-container">
      <div ref="mapEl" class="map"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount, watch, computed } from "vue"
import { scaleLog } from 'd3-scale'
import { interpolateLab } from 'd3-interpolate'
import L from "leaflet"
import "leaflet/dist/leaflet.css"
const defaultView = [37.25, -119.5] // CA center
const cartoApiKey = process.env.VUE_APP_CARTO_API_KEY // get key from app-specific .env file

const props = defineProps({
  geojson: { type: Object, default: null }, // FeatureCollection
  vouchers: { type: Array, default: () => [] }, // normalized vouchers (or filtered)
  geographyType: { type: String, required: true }, // "county" | "airDistrict" | ...
  featureNameProp: { type: String, required: true },
  program: { type: String, default: "core" }, // "core" or "sore" - affects voucher counting logic and KPI display
  voucherRegionGetter: { type: Function, default: (v, geo) => v?.[geo] },
  voucherFundingGetter: { type: Function, default: (v) => Number(v?.raw?.["Funding Amount"] ?? v?.fundingAmount ?? 0) },
  normalizeKey: { type: Function, default: (s) => String(s ?? "").trim() },
  fitBoundsOnLoad: { type: Boolean, default: true }, // Map behavior
})
const emit = defineEmits(["reset-view", "render-complete"])

const waitForPaint = () =>
  new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  
// Helpers -------
function fitToCurrentBounds() {
  if (!mapInstance || !lastBounds) return
  mapInstance.fitBounds(lastBounds, { padding: [10, 10] })
}

function resetView() {
  if (!mapInstance) return
  if (lastBounds) {
    fitToCurrentBounds()
  } else {
    mapInstance.setView(defaultView, 6) // default view
  }
  emit('reset-view') // notify parent that reset occurred
}
defineExpose({ resetView })

function addResetViewControl() {
  resetControl = L.control({ position: 'topleft' })
  resetControl.onAdd = () => {
    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control')
    const btn = L.DomUtil.create('a', 'leaflet-control-reset', container)
    btn.href = '#'
    btn.title = 'Reset map view'
    btn.setAttribute('role', 'button')
    btn.setAttribute('aria-label', 'Reset map view')
    btn.innerHTML = '<i class="fa-solid fa-arrows-rotate"></i>'
    
    // Prevent map interactions when clicking the button
    L.DomEvent.disableClickPropagation(container)
    L.DomEvent.disableScrollPropagation(container)
    L.DomEvent.on(btn, 'click', (e) => {
      L.DomEvent.preventDefault(e)
      resetView()
    })

    return container
  }
  resetControl.addTo(mapInstance)
}

// --- aggregate voucher totals by region ---
const totalsByRegion = computed(() => {
  const out = new Map() // key -> {sum, count, voucherIds}
  const geo = props.geographyType

  for (const v of props.vouchers ?? []) {
    const regionRaw = props.voucherRegionGetter(v, geo)
    if (regionRaw == null || regionRaw === "") continue

    const key = props.normalizeKey(regionRaw)
    const funding = props.voucherFundingGetter(v)
    const prev = out.get(key) ?? { sum: 0, count: 0, voucherIds: new Set() }
    prev.sum += Number.isFinite(funding) ? funding : 0
    prev.count += 1
    if (v.voucherRequestId) {
      prev.voucherIds.add(v.voucherRequestId)
    }
    out.set(key, prev)
  }
  return out
})

function fmtMoney(x) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(x ?? 0)
}


const styles = getComputedStyle(document.documentElement)
// console.log('Computed styles:', styles)

const paletteSequential = [
  styles.getPropertyValue('--seq-color-01').trim(),
  styles.getPropertyValue('--seq-color-02').trim(),
  styles.getPropertyValue('--seq-color-03').trim(),
  styles.getPropertyValue('--seq-color-04').trim(),
  styles.getPropertyValue('--seq-color-05').trim(),
  styles.getPropertyValue('--seq-color-06').trim()
]
// console.log('paletteSequential:', paletteSequential)


function getBreaks(values) {
  const v = values
    .filter(n => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b)
  if (!v.length) return [1, 10] // fallback must also be positive, non-zero
  return [v[0], v[v.length - 1]]
}

function createColorScale(breaks, palette = paletteSequential) {
  const [min, max] = breaks
  const span = max - min
  const steps = palette.length - 1
  // geometric spacing: each stop is min * ratio^i
  const ratio = max / min
  const domain = palette.map((_, i) => min * Math.pow(ratio, i / steps))

   return scaleLog()
    .domain(domain)
    .range(palette)
    .interpolate(interpolateLab)
}

function colorFor(value, colorScale) {
  if (!Number.isFinite(value) || value <= 0) return '#efefef'
  return colorScale(value)
}

function buildLegend(breaks) {
  if (!mapInstance) return
  if (legend) legend.remove()

  const [min, max] = breaks

  legend = L.control({ position: "bottomleft" })

  legend.onAdd = () => {
    const div = L.DomUtil.create("div", "leaflet-control leaflet-bar")

    div.style.background = "rgba(255, 255, 255, 0.7)"
    div.style.padding = "8px 10px"
    div.style.borderRadius = "4px"
    div.style.boxShadow = "0 2px 10px rgba(0,0,0,.15)"
    div.style.fontSize = "0.75rem"
    div.style.minWidth = "150px"
    div.style.zIndex = "700"
    div.innerHTML = `
      <div style="font-weight:700;margin-bottom:2px;">Total Funding</div>

      <div style="
        height: 12px;
        border: 1px solid #ccc;
        border-radius: 999px;
        background: linear-gradient(
          to right,
          ${paletteSequential[0]},
          ${paletteSequential[1]},
          ${paletteSequential[2]},
          ${paletteSequential[3]},
          ${paletteSequential[4]},
          ${paletteSequential[5]}
        );
      "></div>

      <div style="
        display: flex;
        justify-content: space-between;
        gap: 8px;
        margin-top: 2px;
      ">
        <span>${fmtMoney(min)}</span>
        <span>${fmtMoney(max)}</span>
      </div>

      <div style="
        display:flex;
        align-items:center;
        gap:0.3rem;
        margin-top:8px;
        border-radius:2px;
      ">
        <span style="
          display: inline-block;
          width: 0.75rem;
          height: 0.75rem;
          background: #efefef;
          border: 1px solid #ccc;
        "></span>
        <span>None</span>
      </div>
    `

    return div
  }

  legend.addTo(mapInstance)
}

function getFeatureJoinKey(feature) {
  return props.normalizeKey(feature?.properties?.[props.featureNameProp])
}

function getFeatureDisplayName(feature) {
  return getFeatureJoinKey(feature)
}


function onEach(feature, lyr) {
  const name = feature?.properties?.[props.featureNameProp]
  const agg = totalsByRegion.value.get(name) ?? {
    sum: 0,
    count: 0
  }

  lyr.bindTooltip(`
    <div class="leaflet-tooltip-title">${getFeatureDisplayName(feature)}</div>
    <div class="leaflet-tooltip-content">
      <div>Total Funding:&nbsp;&nbsp;
        <span class="leaflet-tooltip-value">
          ${fmtMoney(agg.sum)}
        </span>
      </div>

      ${
        props.program === "sore"
        ? `
          <div>Total Vouchers:&nbsp;
            <span class="leaflet-tooltip-value">
              ${(agg.voucherIds?.size ?? 0).toLocaleString()}
            </span>
          </div>
          <div>Units of Equipment:&nbsp;
            <span class="leaflet-tooltip-value">
              ${agg.count.toLocaleString()}
            </span>
          </div>
        `
        : `
          <div>Total Vouchers:&nbsp;
            <span class="leaflet-tooltip-value">
              ${agg.count.toLocaleString()}
            </span>
          </div>
        `
      }
    </div>
  `,
  {
    sticky: true,
    pane: 'tooltipPane'
  })

  lyr.on("mouseover", () => {
    lyr.setStyle({
      weight: 4,
      opacity: 1,
      fillOpacity: 1
    })
  })

  lyr.on("mouseout", () => {
    layer?.resetStyle?.(lyr)
  })
}


const hasRenderedOnce = ref(false)
let isRendering = false

async function renderLayer() {
  if (!mapInstance) return
  if (!props.geojson?.features?.length) return
  if (hasRenderedOnce.value && !props.vouchers?.length) return
  if (isRendering) return

  isRendering = true
  await nextTick()
  await waitForPaint()

  try {
    const fc = props.geojson
    if (!fc?.features?.length) return

    const values = fc.features.map(f => {
      const name = props.normalizeKey(f?.properties?.[props.featureNameProp])
      return totalsByRegion.value.get(name)?.sum ?? 0
    })

    const breaks = getBreaks(values)
    const colorScale = createColorScale(breaks)

    const styleFn = (feature) => {
      const name = props.normalizeKey(feature?.properties?.[props.featureNameProp])
      const sum = totalsByRegion.value.get(name)?.sum ?? 0
      return {
        weight: 1,
        color: "#ffffff",
        opacity: 1,
        fillOpacity: 0.95,
        fillColor: colorFor(sum, colorScale),
      }
    }

    const newLayer = L.geoJSON(fc, {
      style: styleFn,
      onEachFeature: onEach,
    })

    const newBounds = newLayer.getBounds()
    newLayer.addTo(mapInstance)
    if (layer) layer.remove()
    layer = newLayer
    hasRenderedOnce.value = true

    if (legend) {
      legend.remove()
      legend = null
    }
    buildLegend(breaks)

    if (props.fitBoundsOnLoad) {
      lastBounds = newBounds
      // Register moveend listener BEFORE fitBounds so we never miss it
      const moveEndPromise = new Promise(resolve => mapInstance.once("moveend", resolve))
      mapInstance.fitBounds(newBounds, { padding: [12, 12] })
      await Promise.race([moveEndPromise, new Promise(resolve => setTimeout(resolve, 1000))])
    }

    await waitForPaint()
    await waitForPaint() // two frames to let tiles settle

  } finally {
    await waitForPaint()
    isRendering = false
    emit('render-complete')
  }
}

// Initialize map and controls
const mapEl = ref(null)
let mapInstance = null
let lastBounds = null
let resetControl = null
let resizeObserver = null
let resizeTimeout = null
let layer = null
let legend = null
let initialFitComplete = false

onMounted(async () => {
  // isMapContentUpdating.value = true  // show spinner right away
  await nextTick() // ensure mapEl is rendered/DOM ready
  if (!mapEl.value) return
  mapInstance = L.map(mapEl.value).setView(defaultView, 6) 
  // Base tiles (no labels)
  L.tileLayer(
    `https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png?key=${cartoApiKey}`,
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 20,
      minZoom: 4,
    },
  ).addTo(mapInstance)

  // Labels above polygons
  const labelsPane = mapInstance.createPane('labels')
  labelsPane.style.zIndex = 650
  labelsPane.style.pointerEvents = "none" // allow clicks to pass through labels

  // Labels-only layer
  L.tileLayer(
    `https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png?key=${cartoApiKey}`,
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      pane: 'labels',
      maxZoom: 20,
      minZoom: 4,
    },
  ).addTo(mapInstance)

  renderLayer() // initial render

  const tooltipPane = mapInstance.createPane('tooltipPane')
  tooltipPane.style.zIndex = 900
  tooltipPane.style.pointerEvents = "none" // allow clicks to pass through tooltips


  // Map sizing - ensure map canvas fills container and triggers proper resize events
  requestAnimationFrame(() => {
    if (!mapInstance) return
    mapInstance.invalidateSize()
    if (!initialFitComplete && lastBounds) {
      fitToCurrentBounds()
      initialFitComplete = true
    }
  })

  setTimeout(() => {
    if (!mapInstance) return
    mapInstance.invalidateSize()
    if (!initialFitComplete && lastBounds) {
      fitToCurrentBounds()
      initialFitComplete = true
    }
  }, 500)

  resizeObserver = new ResizeObserver(() => {
    if (!mapInstance) return
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      if (!mapInstance) return
      mapInstance.invalidateSize()
    }, 50)
  })
  if (mapEl.value) {
    resizeObserver.observe(mapEl.value)
  }

  addResetViewControl()
})

watch(
  () => [props.geojson, props.vouchers, props.geographyType, props.featureNameProp],
  async ([newGeojson, newVouchers]) => {
    if (!newGeojson?.features?.length) return
    if (!newVouchers?.length) return // wait for vouchers to arrive before rendering, to avoid unnecessary double renders and flashing
    await renderLayer()
  },
  { deep: false }
)

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
    resizeTimeout = null
  }

  if (legend) {
    legend.remove()
    legend = null
  }

  if (layer) {
    layer.remove()
    layer = null
  }

  if (mapInstance) {
    mapInstance.remove()
    mapInstance = null
  }
})
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 0;
  height: var(--chart-max-height);
  max-height: 80vh;
  box-sizing: border-box;
}

.map {
  width: 100%;
  min-width: 0;
  height: 100%;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.map-loading {
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 20px;
  font-weight: 700;
  pointer-events: none;
}
</style>
